/** A régua de painéis: que entradas existem, e por que ordem.
 *
 *  O problema que resolve. Hoje, para abrir um painel é preciso saber
 *  em que separador do friso está o interruptor dele: a Aparência está
 *  num, os Pins BCF noutro, o Mapa noutro ainda, e os módulos (IDS,
 *  BCF, Conflitos, Assistente) nem sequer estão no friso, estão na
 *  barra de topo. Quem não usa a aplicação todos os dias não tem onde
 *  procurar.
 *
 *  A régua junta tudo numa coluna de ícones, sempre no mesmo sítio.
 *  Não substitui o friso: o friso é para comandos (fazer uma coisa), a
 *  régua é para painéis (abrir um sítio).
 *
 *  A ordem e o que está escondido ficam guardados por browser. Este
 *  ficheiro tem só a lista e as regras, sem React, para se poder
 *  testar a sério.
 */

export type RailItemId =
  | 'aparencia'
  | 'colorir'
  | 'lentes'
  | 'documento'
  | 'ifcx'
  | 'planos-corte'
  | 'filtro'
  | 'viewpoints'
  | 'pins-bcf'
  | 'mapa'
  | 'nuvem'
  | 'ids-painel'
  | 'dados'
  | 'estrutura'
  | 'globo'
  | 'juntar-ifc'
  | 'propriedades'
  | 'arvore'
  // módulos
  | 'mod-bcf'
  | 'mod-ids'
  | 'mod-clash'
  | 'mod-boq'
  | 'mod-info-table'
  | 'mod-ifc-edit'
  | 'mod-assistant'
  | 'mod-pdf-review'

export interface RailItem {
  id: RailItemId
  /** O nome que aparece no rótulo e na personalização. */
  label: string
  /** Uma linha a dizer o que faz, para o title. */
  descricao: string
  /** Módulos abrem uma página; painéis são interruptores locais. */
  tipo: 'painel' | 'modulo'
  /** Para os módulos, o slug do registo. */
  slug?: string
}

/** A ordem por omissão, pensada de cima para baixo: primeiro o que se
 *  usa a olhar para o modelo, depois o que se usa a trabalhar sobre
 *  ele. */
export const RAIL_ITEMS: RailItem[] = [
  {
    id: 'arvore',
    label: 'Estrutura',
    descricao: 'Árvore espacial e lista de entidades do modelo',
    tipo: 'painel',
  },
  {
    id: 'propriedades',
    label: 'Propriedades',
    descricao: 'Atributos, property sets e relações do que está selecionado',
    tipo: 'painel',
  },
  {
    id: 'filtro',
    label: 'Filtro',
    descricao: 'Esconder e isolar por classe, propriedade ou quantidade',
    tipo: 'painel',
  },
  {
    id: 'aparencia',
    label: 'Aparência',
    descricao: 'Pintar elementos, arestas, fantasma e fundo',
    tipo: 'painel',
  },
  {
    id: 'colorir',
    label: 'Colorir por',
    descricao: 'Colorir o modelo por classe, tipo, material ou propriedade',
    tipo: 'painel',
  },
  {
    id: 'lentes',
    label: 'Lentes',
    descricao: 'Ver o modelo por regras: colorir, apagar e esconder',
    tipo: 'painel',
  },
  {
    id: 'documento',
    label: 'Documento',
    descricao: 'Relatório sobre o modelo, impresso em PDF',
    tipo: 'painel',
  },
  {
    id: 'planos-corte',
    label: 'Planos de corte',
    descricao:
      'Vários cortes ao mesmo tempo, por eixo ou por uma face clicada',
    tipo: 'painel',
  },
  {
    id: 'ifcx',
    label: 'IFC5',
    descricao:
      'Abrir .ifcx e empilhar camadas. O formato ainda está em alfa',
    tipo: 'painel',
  },
  {
    id: 'viewpoints',
    label: 'Viewpoints',
    descricao: 'Vistas guardadas da câmara',
    tipo: 'painel',
  },
  {
    id: 'pins-bcf',
    label: 'Pins BCF',
    descricao: 'Marcadores dos tópicos BCF sobre o modelo',
    tipo: 'painel',
  },
  {
    id: 'mapa',
    label: 'Mapa',
    descricao: 'Localização, georreferenciação e federação',
    tipo: 'painel',
  },
  {
    id: 'nuvem',
    label: 'Nuvem de pontos',
    descricao: 'Importar nuvens e medir o desvio ao modelo',
    tipo: 'painel',
  },
  {
    id: 'ids-painel',
    label: 'IDS rápido',
    descricao: 'Validação IDS sobre o modelo carregado',
    tipo: 'painel',
  },
  {
    id: 'dados',
    label: 'Dados IFC',
    descricao: 'Inventário de Psets, importar de Excel, renomear e apagar Psets',
    tipo: 'painel',
  },
  {
    id: 'estrutura',
    label: 'Estrutura IFC',
    descricao: 'Modo avançado: qualquer entidade, atributos, referências e linha STEP',
    tipo: 'painel',
  },
  {
    id: 'globo',
    label: 'Globo 3D',
    descricao: 'Os modelos georreferenciados num globo, com relevo e geoide',
    tipo: 'painel',
  },
  {
    id: 'juntar-ifc',
    label: 'Juntar IFC',
    descricao: 'Juntar vários IFC num só, sem os abrir',
    tipo: 'painel',
  },
  { id: 'mod-bcf', label: 'BCF', descricao: 'Tópicos e comentários BCF', tipo: 'modulo', slug: 'bcf' },
  { id: 'mod-ids', label: 'IDS', descricao: 'Especificações e relatórios IDS', tipo: 'modulo', slug: 'ids' },
  { id: 'mod-clash', label: 'Conflitos', descricao: 'Deteção de conflitos entre modelos', tipo: 'modulo', slug: 'clash' },
  { id: 'mod-boq', label: 'BoQ', descricao: 'Mapa de quantidades', tipo: 'modulo', slug: 'boq' },
  { id: 'mod-info-table', label: 'Tabela', descricao: 'Tabela de dados do modelo', tipo: 'modulo', slug: 'info-table' },
  { id: 'mod-ifc-edit', label: 'IFCEdit', descricao: 'Editar propriedades e gravar IFC', tipo: 'modulo', slug: 'ifc-edit' },
  { id: 'mod-assistant', label: 'Assistente', descricao: 'Perguntas sobre o modelo', tipo: 'modulo', slug: 'assistant' },
  { id: 'mod-pdf-review', label: 'PDF', descricao: 'Rever e marcar desenhos em PDF', tipo: 'modulo', slug: 'pdf-review' },
]

export const RAIL_ORDEM_KEY = 'vn2r.rail.ordem'
export const RAIL_ESCONDIDOS_KEY = 'vn2r.rail.escondidos'

export interface RailPreferencias {
  ordem: RailItemId[]
  escondidos: RailItemId[]
}

interface Armazenamento {
  getItem: (chave: string) => string | null
  setItem: (chave: string, valor: string) => void
}

function armazenamento(): Armazenamento | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function lerLista(
  store: Armazenamento | null,
  chave: string,
): RailItemId[] | null {
  try {
    const bruto = store?.getItem(chave)
    if (!bruto) return null
    const lido: unknown = JSON.parse(bruto)
    if (!Array.isArray(lido)) return null
    const validos = new Set(RAIL_ITEMS.map((i) => i.id))
    return lido.filter(
      (x): x is RailItemId => typeof x === 'string' && validos.has(x as RailItemId),
    )
  } catch {
    // JSON estragado, ou sem onde guardar: volta-se ao que vem de
    // fábrica em vez de deixar a régua vazia.
    return null
  }
}

export function lerPreferencias(
  store: Armazenamento | null = armazenamento(),
): RailPreferencias {
  return {
    ordem: lerLista(store, RAIL_ORDEM_KEY) ?? [],
    escondidos: lerLista(store, RAIL_ESCONDIDOS_KEY) ?? [],
  }
}

export function gravarPreferencias(
  prefs: RailPreferencias,
  store: Armazenamento | null = armazenamento(),
): void {
  try {
    store?.setItem(RAIL_ORDEM_KEY, JSON.stringify(prefs.ordem))
    store?.setItem(RAIL_ESCONDIDOS_KEY, JSON.stringify(prefs.escondidos))
  } catch {
    /* sem onde guardar: a personalização dura esta sessão */
  }
}

/**
 * A lista final, pela ordem guardada, sem os escondidos e sem os
 * módulos que este workspace não tem.
 *
 * Duas regras que não são óbvias:
 *
 *  - uma entrada nova da aplicação, que ainda não esteja na ordem
 *    guardada, entra no fim em vez de desaparecer. Sem isto, quem
 *    personalizasse a régua nunca mais veria nada de novo;
 *  - a ordem guardada pode ter entradas que já não existem (uma
 *    funcionalidade removida), e essas ignoram-se.
 */
export function itensVisiveis(
  prefs: RailPreferencias,
  modulosDisponiveis: readonly string[],
  todos: readonly RailItem[] = RAIL_ITEMS,
): RailItem[] {
  const porId = new Map(todos.map((i) => [i.id, i]))
  const escondidos = new Set(prefs.escondidos)

  const ordenados: RailItem[] = []
  const jaPosto = new Set<RailItemId>()
  for (const id of prefs.ordem) {
    const item = porId.get(id)
    if (!item || jaPosto.has(id)) continue
    ordenados.push(item)
    jaPosto.add(id)
  }
  for (const item of todos) {
    if (!jaPosto.has(item.id)) ordenados.push(item)
  }

  return ordenados.filter((item) => {
    if (escondidos.has(item.id)) return false
    if (item.tipo === 'modulo') {
      return item.slug !== undefined && modulosDisponiveis.includes(item.slug)
    }
    return true
  })
}

/** Move uma entrada uma posição para cima ou para baixo.
 *
 *  Trabalha sobre a lista completa e devolve uma ordem completa, para
 *  a preferência guardada não depender do que está escondido nesse
 *  momento. */
export function mover(
  ordemAtual: readonly RailItemId[],
  id: RailItemId,
  direcao: -1 | 1,
  todos: readonly RailItem[] = RAIL_ITEMS,
): RailItemId[] {
  const completa: RailItemId[] = [
    ...ordemAtual.filter((x) => todos.some((i) => i.id === x)),
    ...todos.map((i) => i.id).filter((x) => !ordemAtual.includes(x)),
  ]
  const i = completa.indexOf(id)
  if (i < 0) return completa
  const j = i + direcao
  if (j < 0 || j >= completa.length) return completa
  const copia = [...completa]
  ;[copia[i], copia[j]] = [copia[j], copia[i]]
  return copia
}

export function alternarEscondido(
  escondidos: readonly RailItemId[],
  id: RailItemId,
): RailItemId[] {
  return escondidos.includes(id)
    ? escondidos.filter((x) => x !== id)
    : [...escondidos, id]
}
